import type { Command } from 'commander';
import { revokeRefreshToken, type OauthRequestOptions } from '../../lib/oauth';
import { loadPersonalCredentials, deletePersonalCredentials } from '../../lib/credentials-store';
import { resolveContext, type ResolvedContext } from '../../lib/global-opts';
import { printWarn } from '../../lib/output';

/**
 * `wport logout` 核心(RFC 7009 revoke + 本機清除)。
 *
 * revoke 失敗(server 不可達、逾時、5xx 等 —— 不管哪一種)都不阻擋登出:使用者下 `logout`
 * 指令的意圖是清掉「這台裝置」的本機 session,伺服器端撤銷只是順手做掉;做不到也要讓本機
 * 憑證照樣消失,不然使用者會卡在「以為登出了但其實沒有」的狀態,之後 personal-client 拿著
 * 同一份憑證繼續發請求。因此這裡刻意 catch 所有例外(不只 NetworkError),只印警告續行。
 *
 * 未登入時直接印訊息、正常返回(exit 0)——冪等,重複執行 logout 不該被當成錯誤。
 */
export async function performLogout(ctx: ResolvedContext): Promise<void> {
	const creds = loadPersonalCredentials();
	if (!creds) {
		process.stdout.write('Not logged in.\n');
		return;
	}

	const opts: OauthRequestOptions = { baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs };
	try {
		await revokeRefreshToken(opts, creds.refresh_token);
	} catch (err) {
		const message = err instanceof Error ? err.message : String(err);
		printWarn(`Failed to revoke the session on the server: ${message}. Removing local credentials anyway.`, ctx.color);
	}

	deletePersonalCredentials();
	process.stdout.write('Logged out.\n');
}

export function registerLogoutCommand(program: Command): void {
	program
		.command('logout')
		.description('Sign out of your wport personal account (revokes the refresh token and clears local credentials)')
		.action(async (_flags: unknown, command: Command) => {
			await performLogout(resolveContext(command));
		});
}
